<template>
    <DataTable :value="userGroups" showGridlines lazy paginator :rows="rows" :totalRecords="totalRecords" :first="first"
        :loading="loading" :filters="filters" filterDisplay="menu" scrollable scrollHeight="calc(100vh - 16rem)"
        :sortField="sortField" :sortOrder="sortOrder" @page="$emit('page', $event)" @filter="$emit('filter', $event)"
        @sort="$emit('sort', $event)"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
        :currentPageReportTemplate="getPaginatorTemplate()">
        <template #loading></template>
        <template #empty>
            <div class="text-center py-12 text-gray-500">
                <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-3">
                    <i class="fa-solid fa-users text-3xl text-gray-400"></i>
                </div>
                <p class="font-semibold text-gray-700">{{ t('userGroups.noUserGroupsFound') }}</p>
            </div>
        </template>

        <template #header>
            <div class="flex justify-between items-center">
                <Button icon="fa-solid fa-plus" :label="t('userGroups.new')" @click="$emit('create')"
                    class="whitespace-nowrap min-w-[140px]" />
                <Button icon="fa-solid fa-filter-circle-xmark" :label="t('clearFilters')"
                    @click="$emit('clearFilters')" />
            </div>
        </template>

        <!-- NAME -->
        <Column field="name" :header="t('userGroups.name')" sortable filter
            :filterMatchModeOptions="textMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <span v-else>{{ data.name }}</span>
            </template>
            <template #filter="{ filterModel }">
                <InputText v-model="filterModel.value" type="text" :placeholder="t('userGroups.name')" />
            </template>
        </Column>

        <!-- DESCRIPTION -->
        <Column field="description" :header="t('userGroups.description')" sortable filter
            :filterMatchModeOptions="textMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="150px" height="1rem" />
                <span v-else>{{ data.description || '-' }}</span>
            </template>
            <template #filter="{ filterModel }">
                <InputText v-model="filterModel.value" type="text" :placeholder="t('userGroups.description')" />
            </template>
        </Column>

        <!-- USERS -->
        <Column field="users" :header="t('userGroups.users')" headerStyle="width: 120px">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100%" height="1rem" />
                <Button v-else :label="`${data.users?.length || 0}`" icon="fa-solid fa-users"
                    :severity="data.users?.length ? 'info' : 'secondary'" outlined size="small" class="w-full"
                    @click="$emit('showUsers', data)" />
            </template>
        </Column>

        <!-- NOTIFICATIONS -->
        <Column field="notifications" :header="t('userGroups.notifications')" headerStyle="width: 120px">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100%" height="1rem" />
                <Button v-else :label="`${getNotificationsCount(data.id)}`" icon="fa-regular fa-bell"
                    :severity="getNotificationsCount(data.id) > 0 ? 'success' : 'secondary'" outlined size="small"
                    class="w-full" @click="$emit('showNotifications', data)" />
            </template>
        </Column>

        <!-- STATUS -->
        <Column field="enabled" :header="t('status.title')" sortable filter headerStyle="width: 130px"
            :filterMatchModeOptions="selectMatchModeOptions">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <Tag v-else :value="data.enabled ? t('common.enabled') : t('common.disabled')"
                    :severity="data.enabled ? 'success' : 'danger'" />
            </template>
            <template #filter="{ filterModel }">
                <Select v-model="filterModel.value" :options="statusOptions" optionLabel="label" optionValue="value"
                    :placeholder="t('status.select')" />
            </template>
        </Column>

        <!-- ACTIONS -->
        <Column headerStyle="width:4rem" :header="t('actions')">
            <template #body="{ data }">
                <Skeleton v-if="loading" width="100px" height="1rem" />
                <div v-else class="flex justify-center">
                    <i class="fa-solid fa-edit fa-lg text-primary cursor-pointer" @click="$emit('edit', data)"></i>
                    <i class="fa-regular fa-trash-can fa-lg text-red-500 cursor-pointer ml-4"
                        @click="$emit('delete', data)"></i>
                </div>
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import { useFilterMatchModeOptions } from '@/composables/useFilterMatchModeOptions';
import type { UserGroup } from '@/lib/types/userGroup';
import type { DataTableFilterEvent, DataTableSortEvent } from 'primevue/datatable';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const {
    textMatchModeOptions,
    selectMatchModeOptions
} = useFilterMatchModeOptions();

interface Props {
    userGroups: UserGroup[];
    loading: boolean;
    filters: any;
    sortField?: string;
    sortOrder: number;
    first: number;
    rows: number;
    totalRecords: number;
    filteredRecords: number;
    notifications: Record<string, any>;
}

const props = defineProps<Props>();

defineEmits<{
    page: [event: any];
    filter: [event: DataTableFilterEvent];
    sort: [event: DataTableSortEvent];
    clearFilters: [];
    create: [];
    edit: [userGroup: UserGroup];
    delete: [userGroup: UserGroup];
    showUsers: [userGroup: UserGroup];
    showNotifications: [userGroup: UserGroup];
}>();

const statusOptions = computed(() => [
    { label: t('common.enabled'), value: true },
    { label: t('common.disabled'), value: false }
]);

function getNotificationsCount(groupId: string): number {
    return Object.values(props.notifications[groupId] || {}).length;
}

function getPaginatorTemplate(): string {
    const hasActiveFilters = props.filteredRecords < props.totalRecords;

    if (hasActiveFilters) {
        return `{first} to {last} of {totalRecords} (filtered from ${props.totalRecords} total records)`;
    }

    return '{first} to {last} of {totalRecords}';
}
</script>
